import React, { FC, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { useQuery } from 'react-query'
import { PageLink, PageTitle, useLayout } from '../../../_metronic/layout/core'
import moment from 'moment'
import { KTIcon } from '../../../_metronic/helpers'
import { Column, useFilters, useGlobalFilter, usePagination, useSortBy, useTable } from 'react-table'
import ModalEditUser from '../../../_metronic/partials/widgets/_new/modal/ModalEditUser'
import { axiosClientAuth } from '../../utils'

type UserType = {
  uuid: string
  name: string
  email: string
  phone?: string
  created_at: string
  group_obj: {
    id?: number
    name: string
    uuid?: string
  }
}

const EMPTY_USERS: UserType[] = []

const UserManagement: FC = () => {
  const intl = useIntl()
  const [currData, setCurrData] = useState<UserType | null>(null)
  const [show, setShow] = useState(false)

  const { data: users = EMPTY_USERS, isLoading } = useQuery(
    ['users'],
    () =>
      axiosClientAuth
        .get('/auth/users/', {
          params: { type: 'AD', page: 1, page_size: 200 },
          transformResponse: [
            (raw: string) => {
              const safe = raw.replace(/"id"\s*:\s*(\d{15,})/g, '"id":"$1"')
              return JSON.parse(safe)
            },
          ],
        })
        .then((res) =>
          (res.data?.data?.results ?? []).map((item: any) => ({
            ...item,
            uuid: item.id,
          })) as UserType[]
        )
  )

  const deleteUser = (user: UserType) => {
    // TODO: integrate new delete API
  }

  const columns: Column<UserType>[] = useMemo(
    () => [
      {
        Header: intl.formatMessage({ id: 'TABLE.SL_NO', defaultMessage: 'S.No' }),
        id: 'serial-number',
        Cell: ({ row }) => row.index + 1,
      } as unknown as Column<UserType>,
      {
        Header: intl.formatMessage({ id: 'TABLE.NAME', defaultMessage: 'Name' }),
        accessor: 'name',
      },
      {
        Header: intl.formatMessage({ id: 'TABLE.EMAIL', defaultMessage: 'Email ID' }),
        accessor: 'email',
      },
      {
        Header: intl.formatMessage({ id: 'TABLE.ROLES', defaultMessage: 'Role' }),
        accessor: (row) => row.group_obj?.name,
        Cell: ({ value }) => (
          <span className='badge badge-light-primary fs-7 m-1'>{value || 'N/A'}</span>
        ),
      },
      {
        Header: intl.formatMessage({ id: 'TABLE.CREATED_AT', defaultMessage: 'Created At' }),
        accessor: 'created_at',
        Cell: ({ value }) => (
          <>
            <div>{moment(value).format('DD MMM, YYYY')}</div>
            <div className='text-muted'>{moment(value).format('hh:mm A')}</div>
          </>
        ),
      },
      {
        Header: intl.formatMessage({ id: 'TABLE.ACTIONS', defaultMessage: 'Actions' }),
        id: 'actions',
        Cell: ({ row }) => {
          const user = row.original
          return (
            <div className='d-flex justify-content-start'>
              <button
                className='btn btn-icon btn-active-light-primary w-30px h-30px me-2'
                onClick={() => {
                  setCurrData(user)
                  setShow(true)
                }}
              >
                <KTIcon iconName='notepad-edit' className='fs-4' />
              </button>
              <button
                className='btn btn-icon btn-active-light-danger w-30px h-30px'
                onClick={() => deleteUser(user)}
              >
                <KTIcon iconName='trash' className='fs-4' />
              </button>
            </div>
          )
        },
      },
    ],
    [intl]
  )

  const table = useTable(
    { columns, data: users, autoResetPage: false },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination
  )

  return (
    <>
      <div className='card'>
        <div className='card-header border-0 pt-5'>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bold fs-3 mb-1'>{intl.formatMessage({ id: 'USER.TITLE', defaultMessage: 'User Management' })}</span>
            <span className='text-muted fw-semibold fs-7'>{intl.formatMessage({ id: 'USER.TOTAL_USERS', defaultMessage: 'Total {count} Users' }, { count: users.length })}</span>
          </h3>
          <div className='d-flex align-items-center'>
            <input
              type='text'
              className='form-control form-control-sm w-200px me-3'
              placeholder={intl.formatMessage({ id: 'USER.SEARCH_USER', defaultMessage: 'Search User' })}
              value={table.state.globalFilter ?? ''}
              onChange={(e) => table.setGlobalFilter(e.target.value)}
            />
          </div>
        </div>
        <div className='card-body pt-3'>
          <div className='table-responsive'>
            <table
              {...table.getTableProps()}
              className='table align-middle table-row-dashed fs-6 gy-5 dataTable no-footer'
            >
              <thead>
                {table.headerGroups.map((headerGroup) => (
                  <tr {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column) => (
                      <th {...column.getHeaderProps(column.getSortByToggleProps())} className='text-uppercase'>
                        {column.render('Header')}
                        {column.isSorted ? (
                          column.isSortedDesc ? (
                            <KTIcon iconName='down' className='ms-1 text-muted' />
                          ) : (
                            <KTIcon iconName='up' className='ms-1 text-muted' />
                          )
                        ) : null}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...table.getTableBodyProps()}>
                {isLoading ? <tr>
                  <td colSpan={columns.length} className='text-center py-6'>
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </td>
                </tr> : (table.page.length > 0 && (
                  table.page.map((row) => {
                    table.prepareRow(row)
                    return (
                      <tr {...row.getRowProps()}>
                        {row.cells.map((cell) => (
                          <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                        ))}
                      </tr>
                    )
                  })
                )) || (
                  <tr>
                    <td colSpan={columns.length} className='text-center'>
                      <span className='fw-bold'>No data found</span>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            {/* Pagination Controls */}
            <div className='d-flex justify-content-between align-items-center mt-5'>
              <div>
                <select
                  className='form-select form-select-sm form-select-solid'
                  value={table.state.pageSize}
                  onChange={(e) => table.setPageSize(Number(e.target.value))}
                >
                  {[10, 25, 50, 100].map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                Page <strong>{table.state.pageIndex + 1} of {table.pageOptions.length}</strong>
              </div>
              <div>
                <button
                  className='btn btn-sm '
                  onClick={() => table.previousPage()}
                  disabled={!table.canPreviousPage}
                >
                  <KTIcon iconName='left' className='text-gray fs-1 me-5' />
                </button>
                <button
                  className='btn btn-sm '
                  onClick={() => table.nextPage()}
                  disabled={!table.canNextPage}
                >
                  <KTIcon iconName='right' className='text-gray fs-1 me-5' />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      {show && currData && (
        <ModalEditUser show={show} setShow={setShow} currData={currData} />
      )}
    </>
  )
}

const myBreadCrumbs: PageLink[] = [
  { title: 'Home', path: '/dashboard', isSeparator: false, isActive: false },
  { title: '', path: '', isSeparator: true, isActive: false },
]

const UserManagementWrapper: FC = () => {
  const intl = useIntl()
  const { setRightTolbar } = useLayout()

  useEffect(() => {
    setRightTolbar('usermanagement')
  }, [])

  return (
    <>
      <PageTitle breadcrumbs={myBreadCrumbs}>{intl.formatMessage({ id: 'PAGE.USER_MANAGEMENT', defaultMessage: 'User Management' })}</PageTitle>
      <UserManagement />
    </>
  )
}

export { UserManagementWrapper }
